import { render } from 'preact';
import 'preact/devtools';
import { useLayoutEffect, useState } from 'preact/hooks';
import './App.less';
import Assistant from './components/assistant/Assistant';
import store, { type AppDispatch } from './store/store';
import { StoreContext } from './contexts/StoreContext';
import { getConnector, setConnector } from './services/Connector';
import ProductComparisonToolbar from './components/main/comparison/ProductComparisonToolbar';
import { isChosenForSampling } from './services/SamplingService';
import { initialize as initializeTranslations } from './services/TranslationService';
import { addLogger, log, error } from './logger/Logger';
import './api';
import { apiClient } from './services/ApiClient';
import {
    getInteractionPageViewFromPageInfo,
    getPageInfo
} from './services/PageInfoService';
import { initializeIndexedDbService } from './services/IndexedDbService';
import {
    addInteraction,
    setFreeShippingPolicy
} from './services/ActiveHelpService';
import { dispatchEvent } from './services/EventService';
import type { ClientTranslations } from '@resources/client/ClientTranslations';
import { addGoogleAnalyticsId } from './services/GoogleAnalyticsService';

if (ENABLE_LOGGER_CONSOLE) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    addLogger(require('./logger/ConsoleLogger').consoleLogger);
}
if (ENABLE_LOGGER_APPLICATION_INSIGHTS) {
    addLogger(
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        require('./logger/ApplicationInsightsLogger').applicationInsightsLogger
    );
}
if (CONNECTOR === 'test-web') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    setConnector(require('./connectors/TestWebConnector').testWebConnector);
}
if (CONNECTOR === 'shopify') {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    setConnector(require('./connectors/ShopifyConnector').shopifyConnector);
}

const App = () => {
    return (
        <div className="bri-App algo-bonito">
            <Assistant />
            <ProductComparisonToolbar />
        </div>
    );
};

const StoreProvider = () => {
    const [state, setState] = useState(store.getState());
    useLayoutEffect(() => {
        store.subscribe(() => {
            setState(store.getState());
        });
    }, []);
    return (
        <StoreContext.Provider value={state}>
            <App />
        </StoreContext.Provider>
    );
};

const initializeStatesAndServices = async (dispatch: AppDispatch) => {
    const pageInfo = getPageInfo();
    dispatch.contextState.setPageInfo(pageInfo);
    const [
        {
            jsonResult: {
                product,
                googleAnalyticsIds,
                freeShippingPolicy,
                tenantId,
                resources,
                aspectRatio,
                discountBadge
            }
        }
    ] = await Promise.all([
        apiClient.assistant.fetchAssistantConfig({
            body: {
                pageInfo,
                accountId: ACCOUNT_ID
            },
            options: {
                authenticated: false
            }
        }),
        initializeIndexedDbService(),
        dispatch.cartState.reloadCurrentCart()
    ]);
    (googleAnalyticsIds ?? []).forEach(addGoogleAnalyticsId);
    dispatch.uiState.setAspectRatio(aspectRatio);
    dispatch.uiState.setPosition(
        document
            .querySelector('[data-bri-position]')
            ?.getAttribute('data-bri-position')
            ?.trim() ?? 'right'
    );
    dispatch.uiState.setDiscountBadge(discountBadge);
    if (!tenantId) {
        return;
    }
    apiClient.setTenantId(tenantId);
    initializeTranslations(resources as ClientTranslations);
    dispatch.uiState.setMode('button');
    setFreeShippingPolicy(freeShippingPolicy);
    let counter = 0;
    setInterval(async () => {
        if (document.visibilityState === 'hidden') {
            return;
        }
        counter += 1;
        if (counter % 10 === 0) {
            await addInteraction({
                ...getInteractionPageViewFromPageInfo({
                    pageInfo,
                    product
                }),
                type: 'page-active',
                seconds: counter
            });
        }
    }, 1000);
    dispatchEvent({
        type: 'bri.init_success',
        isInteraction: false
    });
    // Dispatching the show main button event as different event from init success
    // to allow the client to handle it separately if needed in the future when
    // the main button become optional.
    dispatchEvent({
        type: 'bri.show_main_button',
        isInteraction: false
    });
    await addInteraction(
        getInteractionPageViewFromPageInfo({
            pageInfo,
            product
        })
    );
    getConnector().onCartUpdated(() => dispatch.cartState.reloadCurrentCart());

    const productListCarouselPlaceholders =
        document.querySelectorAll<HTMLElement>(
            '[data-bri-product-list-carousel]'
        );
    productListCarouselPlaceholders.forEach(placeholder => {
        window.brilliant.createProductListCarousel({
            productId: placeholder.getAttribute(
                'data-bri-product-id'
            ) as string,
            searchType: placeholder.getAttribute('data-bri-search-type') as
                | 'combination'
                | 'similar',
            parent: placeholder,
            title: placeholder.getAttribute('data-bri-title') as string
        });
    });

    const productPackBannerPlaceholders =
        document.querySelectorAll<HTMLElement>(
            '[data-bri-product-pack-banner]'
        );
    productPackBannerPlaceholders.forEach(placeholder => {
        window.brilliant.createProductPackBanner({
            productId: placeholder.getAttribute(
                'data-bri-product-id'
            ) as string,
            parent: placeholder,
            title: placeholder.getAttribute('data-bri-title') as string
        });
    });

    const productComparisonTablePlaceholders =
        document.querySelectorAll<HTMLElement>(
            '[data-bri-product-comparison-table]'
        );
    productComparisonTablePlaceholders.forEach(placeholder => {
        window.brilliant.createProductComparisonSummary({
            productIds: [
                placeholder.getAttribute('data-bri-product-id') as string
            ],
            parent: placeholder,
            title: placeholder.getAttribute('data-bri-title') as string
        });
    });

    const productQuestionsPlaceholders = document.querySelectorAll<HTMLElement>(
        '[data-bri-product-questions]'
    );
    productQuestionsPlaceholders.forEach(placeholder => {
        window.brilliant.createProductQuestions({
            productId: placeholder.getAttribute(
                'data-bri-product-id'
            ) as string,
            parent: placeholder
        });
    });

    const searchByImageButtons = document.querySelectorAll<HTMLElement>(
        '[data-bri-search-by-image-button]'
    );
    searchByImageButtons.forEach(button => {
        button.addEventListener('click', event => {
            event.preventDefault();
            return dispatch.featureState.startFeature({
                featureType: 'product-search-by-image-menu',
                searchTypes: ['similar', 'combinations']
            });
        });
    });

    const searchByTextButtons = document.querySelectorAll(
        '[data-bri-search-by-text-button]'
    );
    searchByTextButtons.forEach(button => {
        button.addEventListener('click', event => {
            event.preventDefault();
            const input = document.querySelector<HTMLInputElement>(
                'input[data-bri-search-by-text-input]'
            );
            const searchText = input?.value;
            return dispatch.featureState.startFeature(
                searchText
                    ? { featureType: 'product-search-by-text', searchText }
                    : { featureType: 'product-search-by-text-menu' }
            );
        });
    });
};

const bootstrap = (querySelector = '.bri-placeholder') => {
    if (!isChosenForSampling()) {
        log('Brilliant: client is not chosen for sampling');
        return;
    }
    initializeStatesAndServices(store.dispatch)
        .then(() => {
            let target = document.querySelector(querySelector);
            if (!target) {
                target = document.createElement('div');
                document.body.append(target);
            }
            render(<StoreProvider />, target);
            window.brilliant.initializeProductContextualButtons();

            const searchBannerPlaceholders =
                document.querySelectorAll<HTMLElement>(
                    '[data-bri-search-banner]'
                );
            const searchText = document
                .querySelector('[data-bri-search-text]')
                ?.getAttribute('data-bri-search-text') as string;
            searchBannerPlaceholders.forEach(placeholder => {
                window.brilliant.createSearchBanner({
                    searchText,
                    parent: placeholder
                });
            });
        })
        .catch(err => {
            error('Brilliant: initialization failed', err);
        });
};

if (document.readyState === 'interactive') {
    bootstrap();
} else {
    document.addEventListener('DOMContentLoaded', () => {
        bootstrap();
    });
}
